import { useState, type FormEvent } from "react";
import { useSeo } from "@/hooks/useSeo";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db6f8tjghk82orf0ckt0";

const team = [
  {
    name: "内容策划组",
    role: "影单与专题",
    desc: "负责影视资料的整理、分类与专题影单策划，让好作品被更多人看到。",
    icon: "ri-edit-2-line",
  },
  {
    name: "产品技术组",
    role: "平台体验",
    desc: "打磨筛选、搜索与页面体验，让找片这件小事变得更简单顺手。",
    icon: "ri-code-s-slash-line",
  },
  {
    name: "用户运营组",
    role: "社区与反馈",
    desc: "倾听用户的声音，收集反馈建议，持续优化平台内容与服务。",
    icon: "ri-customer-service-2-line",
  },
];

const notices = [
  "本站为影视信息展示平台，所有影视资料均为演示数据，不代表真实作品信息。",
  "本站不提供任何内容存储与在线播放服务，页面中的播放相关内容均不涉及。",
  "收藏记录保存在你的本地浏览器中，清除浏览器数据后收藏将会丢失。",
  "搜索、筛选与排序功能基于本站演示数据在前端计算完成，结果仅供参考。",
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [message, setMessage] = useState("");

  useSeo({
    title: "关于我们 - 兔六影院",
    description:
      "了解兔六影院的平台定位、运营团队与服务范围，并可通过反馈表单联系我们，共同完善影视内容体验。",
    keywords: "兔六影院,关于我们,影视平台,意见反馈,联系方式",
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，感谢你的反馈，我们会尽快处理。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value as string);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("提交成功，感谢你的反馈，我们会尽快处理。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div>
      <div className="bg-primary-900 py-10 md:py-16">
        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
          <span className="text-sm font-medium text-accent-400">关于兔六影院</span>
          <h1 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-4xl">
            我们只做一件事：帮你找到想看的影视
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-primary-200 md:text-base">
            兔六影院是一个面向广大影迷的影视内容门户，聚合电影与电视剧信息，
            通过清晰的分类、精准的筛选与便捷的搜索，帮助用户在庞大的影视世界里快速找到感兴趣的作品。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              平台定位
            </h2>
            <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
              我们相信，观影的第一步是“找到”。兔六影院围绕电影与电视剧两大板块，
              建立了涵盖类型、年份、地区、评分的多维内容体系，让每一位用户都能按自己的喜好浏览、
              筛选与发现。无论是追逐最新上线的热门作品，还是重温经典的旧时光，
              这里都能给你一份清晰的观影地图。
            </p>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              运营团队
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              一支热爱影视、也热爱打磨产品的小团队
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {team.map((member) => (
                <div
                  key={member.name}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${member.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                    {member.name}
                  </h3>
                  <p className="text-xs font-medium text-accent-600">{member.role}</p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {member.desc}
                  </p>
                </div>
              ))}
            </div>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              用户使用须知
            </h2>
            <ul className="mt-4 space-y-3">
              {notices.map((n) => (
                <li key={n} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-500"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                联系方式
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-accent-600"></i>
                  support@tuliucinema.com
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line text-accent-600"></i>
                  400-666-8899
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-accent-600"></i>
                  北京市朝阳区影世界大厦 18 层
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-accent-600"></i>
                  周一至周五 10:00 - 19:00
                </li>
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                意见反馈
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                有想看的影视或对平台有建议？留言告诉我们吧。
              </p>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-4 space-y-4"
              >
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1 block text-sm font-medium text-foreground-700"
                  >
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入称呼"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-foreground-700"
                  >
                    邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="type"
                    className="mb-1 block text-sm font-medium text-foreground-700"
                  >
                    反馈类型
                  </label>
                  <select
                    id="type"
                    name="type"
                    defaultValue="内容建议"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none cursor-pointer"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="功能反馈">功能反馈</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="商务合作">商务合作</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-1 block text-sm font-medium text-foreground-700"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请填写你的建议或想看的影视（500 字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                </div>

                <input
                  className="form-honeypot-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i>
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>

                {status !== "idle" && status !== "submitting" && message && (
                  <p
                    className={`text-sm ${
                      status === "success" ? "text-primary-600" : "text-accent-700"
                    }`}
                  >
                    {message}
                  </p>
                )}
              </form>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}